import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { usePermissions } from '../hooks/usePermissions'
import { PageContent } from '../components/Layout'
import LoadingSpinner from '../components/LoadingSpinner'
import { CustomersIcon } from '../components/Icons'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { API_BASE_URL } from '../utils/api'
import { PermissionMatrix } from '../components/TeamManagement/PermissionMatrix'
import { TeamAnalyticsDashboard } from '../components/TeamManagement/TeamAnalyticsDashboard'
import { DepartmentManager } from '../components/TeamManagement/DepartmentManager'
import { PermissionGate } from '../components/PermissionGate'
import { useTranslation } from 'react-i18next'

interface Permission {
  viewOnly: boolean
  editTasks: boolean
  editDeals: boolean
  editCustomers: boolean
  manageSms: boolean
}

interface Collaborator {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  permissions: Permission
  status: 'active' | 'inactive' | 'suspended'
  joined_at: string
  last_active_at?: string
}

interface Invite {
  id: number
  phone_number: string
  permissions: Permission
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  created_at: string
  sent_at?: string
  expires_at: string
}

const UsersManagement: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { permissions } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('team')

  // Invite form state
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedRole, setSelectedRole] = useState('collaborator_default')
  const [permissionTemplates, setPermissionTemplates] = useState<any>({})
  const [invitePermissions, setInvitePermissions] = useState<Permission>({
    viewOnly: false,
    editTasks: true,
    editDeals: true,
    editCustomers: true,
    manageSms: false
  })

  useEffect(() => {
    if (user?.role === 'broker') {
      fetchData()
    }
  }, [user])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [collaboratorsRes, invitesRes, templatesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/collaborators`),
        axios.get(`${API_BASE_URL}/api/invites`),
        axios.get(`${API_BASE_URL}/api/invites/permission-templates`)
      ])
      
      setCollaborators(collaboratorsRes.data.collaborators || [])
      setInvites(invitesRes.data.invites || [])
      setPermissionTemplates(templatesRes.data.templates || {})
      
      // Set initial permissions based on default role
      if (templatesRes.data.templates?.collaborator_default) {
        setInvitePermissions(templatesRes.data.templates.collaborator_default.permissions)
      }
    } catch (error: any) {
      console.error('Error fetching data:', error)
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!phoneNumber.trim()) {
      setError(t('teamManagement.inviteModal.phoneRequired'))
      return
    }

    try {
      setSubmitting(true)
      setError('')

      const response = await axios.post(`${API_BASE_URL}/api/invites`, {
        phoneNumber: phoneNumber.trim(),
        role: selectedRole,
        permissions: invitePermissions
      })

      // Reset form
      setPhoneNumber('')
      setSelectedRole('collaborator_default')
      // Reset to default role permissions
      if (permissionTemplates.collaborator_default) {
        setInvitePermissions(permissionTemplates.collaborator_default.permissions)
      }
      setShowInviteForm(false)

      // Refresh data to show new invite
      await fetchData()

      // Show success message (you could add a toast notification here)
      console.log('Invite sent successfully:', response.data)

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || t('teamManagement.inviteModal.error')
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelInvite = async (inviteId: number) => {
    if (!confirm(t('teamManagement.pendingInvites.confirmCancel'))) {
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/invites/${inviteId}`)
      await fetchData() // Refresh data
    } catch (error: any) {
      console.error('Error cancelling invite:', error)
      setError('Failed to cancel invite')
    }
  }

  const handleRoleChange = (role: string) => {
    setSelectedRole(role)
    if (permissionTemplates[role]) {
      setInvitePermissions(permissionTemplates[role].permissions)
    }
  }

  const handlePermissionChange = (key: keyof Permission) => {
    setInvitePermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const formatPhoneNumber = (phone: string) => {
    // Basic phone formatting for display
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', symbol: '✓' },
      inactive: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', symbol: '◯' },
      suspended: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', symbol: '⚠' },
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', symbol: '⏱' },
      accepted: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', symbol: '✓' },
      expired: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', symbol: '✗' },
      cancelled: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', symbol: '✗' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.symbol}</span>
        {t(`teamManagement.status.${status}`)}
      </span>
    )
  }

  if (user?.role !== 'broker') {
    return (
      <PageContent>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('teamManagement.accessDenied.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('teamManagement.accessDenied.message')}</p>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('teamManagement.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('teamManagement.subtitle')}
            </p>
          </div>
          
          {activeTab === 'team' && (
            <button
              onClick={() => setShowInviteForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200"
            >
              <span className="mr-2">+</span>
              {t('teamManagement.sendInvite')}
            </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('team')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'team'
                  ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              👥 {t('teamManagement.tabs.teamInvites')}
            </button>
            
            <PermissionGate permission="manageTeam">
              <button
                onClick={() => setActiveTab('permissions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'permissions'
                    ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                🔐 {t('teamManagement.tabs.permissionMatrix')}
              </button>
            </PermissionGate>

            <PermissionGate permission="viewTeamAnalytics">
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'analytics'
                    ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                📊 {t('teamManagement.tabs.teamAnalytics')}
              </button>
            </PermissionGate>

            <PermissionGate permission="manageTeam">
              <button
                onClick={() => setActiveTab('departments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'departments'
                    ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                🏢 {t('teamManagement.tabs.departments')}
              </button>
            </PermissionGate>
          </nav>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="mt-8">
          {/* Team & Invites Tab */}
          {activeTab === 'team' && (
            <div className="space-y-8">
              {/* Active Collaborators */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('teamManagement.activeCollaborators.title')} ({collaborators.length})
                </h2>
            
            {collaborators.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <CustomersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('teamManagement.activeCollaborators.noCollaborators')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {t('teamManagement.activeCollaborators.noCollaboratorsDesc')}
                </p>
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  <span className="mr-2">+</span>
                  {t('teamManagement.activeCollaborators.sendFirstInvite')}
                </button>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.activeCollaborators.tableHeaders.collaborator')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.activeCollaborators.tableHeaders.contact')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.activeCollaborators.tableHeaders.permissions')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.activeCollaborators.tableHeaders.status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.activeCollaborators.tableHeaders.joined')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {collaborators.map((collaborator) => (
                        <tr key={collaborator.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                <CustomersIcon className="w-5 h-5 text-gray-900 dark:text-gray-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {collaborator.first_name} {collaborator.last_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div>{collaborator.email}</div>
                            <div>{formatPhoneNumber(collaborator.phone)}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex flex-wrap gap-1">
                              {collaborator.permissions.viewOnly && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                  {t('teamManagement.permissions.viewOnly')}
                                </span>
                              )}
                              {collaborator.permissions.editTasks && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {t('teamManagement.permissions.editTasks')}
                                </span>
                              )}
                              {collaborator.permissions.editDeals && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  {t('teamManagement.permissions.editDeals')}
                                </span>
                              )}
                              {collaborator.permissions.editCustomers && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                  {t('teamManagement.permissions.editCustomers')}
                                </span>
                              )}
                              {collaborator.permissions.manageSms && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                  {t('teamManagement.permissions.manageSms')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(collaborator.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(collaborator.joined_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Pending Invites */}
          {invites.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('teamManagement.pendingInvites.title')} ({invites.filter(i => i.status === 'pending').length})
              </h2>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.pendingInvites.tableHeaders.phoneNumber')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.pendingInvites.tableHeaders.permissions')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.pendingInvites.tableHeaders.status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.pendingInvites.tableHeaders.expires')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {t('teamManagement.pendingInvites.tableHeaders.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {invites.map((invite) => (
                        <tr key={invite.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatPhoneNumber(invite.phone_number)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex flex-wrap gap-1">
                              {invite.permissions.viewOnly && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                  {t('teamManagement.permissions.viewOnly')}
                                </span>
                              )}
                              {invite.permissions.editTasks && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {t('teamManagement.permissions.editTasks')}
                                </span>
                              )}
                              {invite.permissions.editDeals && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  {t('teamManagement.permissions.editDeals')}
                                </span>
                              )}
                              {invite.permissions.editCustomers && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                  {t('teamManagement.permissions.editCustomers')}
                                </span>
                              )}
                              {invite.permissions.manageSms && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                  {t('teamManagement.permissions.manageSms')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(invite.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(invite.expires_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {invite.status === 'pending' && (
                              <button
                                onClick={() => handleCancelInvite(invite.id)}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                {t('teamManagement.pendingInvites.cancel')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
            </div>
          )}

          {/* Permission Matrix Tab */}
          <PermissionGate permission="manageTeam">
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    🔐 {t('teamManagement.permissionMatrix.title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t('teamManagement.permissionMatrix.description')}
                  </p>
                  <PermissionMatrix />
                </div>
              </div>
            )}
          </PermissionGate>

          {/* Team Analytics Tab */}
          <PermissionGate permission="viewTeamAnalytics">
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    📊 {t('teamManagement.analytics.title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t('teamManagement.analytics.description')}
                  </p>
                  <TeamAnalyticsDashboard />
                </div>
              </div>
            )}
          </PermissionGate>

          {/* Departments Tab */}
          <PermissionGate permission="manageTeam">
            {activeTab === 'departments' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    🏢 {t('teamManagement.departments.title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t('teamManagement.departments.description')}
                  </p>
                  <DepartmentManager />
                </div>
              </div>
            )}
          </PermissionGate>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('teamManagement.inviteModal.title')}
              </h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </div>
              )}

              <form onSubmit={handleSendInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.inviteModal.phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('teamManagement.inviteModal.phonePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.inviteModal.role')}
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {Object.entries(permissionTemplates).map(([key, template]: [string, any]) => (
                      <option key={key} value={key}>
                        {template.name} - {template.description}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('teamManagement.inviteModal.roleDescription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('teamManagement.inviteModal.permissionsTitle')} {selectedRole !== 'collaborator_default' && t('teamManagement.inviteModal.customizable')}
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="viewOnly"
                        checked={invitePermissions.viewOnly}
                        onChange={() => handlePermissionChange('viewOnly')}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <label htmlFor="viewOnly" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('teamManagement.inviteModal.viewOnlyDesc')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editTasks"
                        checked={invitePermissions.editTasks}
                        onChange={() => handlePermissionChange('editTasks')}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <label htmlFor="editTasks" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('teamManagement.inviteModal.editTasksDesc')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editDeals"
                        checked={invitePermissions.editDeals}
                        onChange={() => handlePermissionChange('editDeals')}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <label htmlFor="editDeals" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('teamManagement.inviteModal.editDealsDesc')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editCustomers"
                        checked={invitePermissions.editCustomers}
                        onChange={() => handlePermissionChange('editCustomers')}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <label htmlFor="editCustomers" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('teamManagement.inviteModal.editCustomersDesc')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="manageSms"
                        checked={invitePermissions.manageSms}
                        onChange={() => handlePermissionChange('manageSms')}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <label htmlFor="manageSms" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('teamManagement.inviteModal.manageSmsDesc')}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false)
                      setError('')
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    {t('teamManagement.inviteModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-blue-400 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
                  >
                    {submitting ? t('teamManagement.inviteModal.sending') : t('teamManagement.inviteModal.sendInvite')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default UsersManagement